/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * MessageBox Component
 * -----------
 * This component displays a colored alert/message box with:
 * - A thick left border colored according to the message type (success, error, warn)
 * - Responsive width and padding using inline styles and Bootstrap's mx-auto to center horizontally
 * - Uses theme colors and border radius from your app's theme context for consistent styling
 * - Accessible with role="alert" and ARIA live region attributes for screen readers
 * Props:
 * - messageType: 'success' | 'error' | 'warn' | 'info' (string) -- determines border color and label shown
 * - message: string -- the message text displayed inside the box
 */

import React from 'react';
import { useTheme } from '../theme/ThemeContext';

const MessageBox = ({ messageType, message }) => {
  const { theme } = useTheme();

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fontSize = windowWidth < 600 ? '1rem' : '1.1rem';
  const padding = windowWidth < 600 ? '16px 20px' : '32px 40px';
  const labelFontSize = windowWidth < 600 ? '1.2rem' : '1.5rem';
  const maxWidth = windowWidth < 600 ? '90%' : '600px';

  const colors = {
    success: 'green',
    error: 'red',
    warn: 'orange',
    info: theme.primary
  };

  const labels = {
    success: 'Success',
    error: 'Error',
    warn: 'Warning',
    info: 'Information'
  };

  const borderColor = colors[messageType] || theme.primary;
  const label = labels[messageType] || 'Info';

  return (
    <div
      className="mx-auto" 
      style={{
        borderLeft: `8px solid ${borderColor}`, 
        backgroundColor: '#fff',
        color: '#333',
        borderRadius: theme.cardRadius,
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        padding: padding,
        maxWidth: maxWidth,
        width: '100%',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontSize: fontSize,
        lineHeight: 1.6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        boxSizing: 'border-box',
      }}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <strong style={{ fontSize: labelFontSize, marginBottom: '16px' }}>{label}</strong>
      <span>{message}</span>
    </div>
  );
};

export default MessageBox;
